import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { CATEGORIES } from "@/pages/admin/CandidateForm";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

const TEXT = [
  ["name", "Full name", "text", true], ["email", "Email", "email", true], ["password", "Create a password (min 6)", "password", true],
  ["phone", "Phone", "tel", false], ["title", "Job title (e.g. Virtual Assistant)", "text", true], ["location", "City, Country", "text", false],
  ["timezone", "Timezone (e.g. PHT UTC+8)", "text", false], ["hourly_rate", "Expected hourly rate (USD)", "number", false],
  ["experience_years", "Years of experience", "number", false], ["skills", "Key skills (comma separated)", "text", false],
];

export default function Apply() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [f, setF] = useState({ category: "Admin Support", availability: "Full-time", summary: "" });
  const [resume, setResume] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) return setError("Please attach your resume");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v ?? ""));
    fd.append("resume", resume);
    licenses.forEach((l) => fd.append("licenses", l));
    setBusy(true);
    setError("");
    try {
      await api.post("/applications", fd);
      await refresh();
      nav("/candidate");
    } catch (err) {
      setError(formatErr(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      <header className="border-b border-stone-200 bg-white"><div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4"><Logo /><Link to="/login" className="text-sm text-stone-600 hover:text-stone-900" data-testid="apply-login-link">Already applied? Sign in</Link></div></header>
      <div className="mx-auto max-w-5xl px-5 py-14 rise">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-700">Join our talent pool</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900">Apply as a virtual worker</h1>
        <p className="mt-3 max-w-2xl text-stone-600">Submit your resume and licenses securely. Our recruiters review every application. Once approved, your profile is shown to member businesses who can send you offers.</p>
        <form onSubmit={submit} data-testid="apply-form" className="mt-10 grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="grid gap-4 rounded-md border border-stone-200 bg-white p-6 sm:grid-cols-2 sm:p-8">
            {TEXT.map(([k, label, type, req]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label><Input data-testid={`apply-${k}-input`} type={type} required={req} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
            ))}
            <div className="space-y-1.5"><Label>Role category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="apply-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Availability</Label>
              <Select value={f.availability} onValueChange={(v) => setF({ ...f, availability: v })}><SelectTrigger data-testid="apply-availability-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Full-time">Full-time</SelectItem><SelectItem value="Part-time">Part-time</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>About you</Label><Textarea data-testid="apply-summary-input" rows={4} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /></div>
          </div>
          <div className="h-fit space-y-4 rounded-md border border-stone-200 bg-[#F5F5F0] p-6">
            <p className="flex items-center gap-2 font-display text-lg font-bold"><Upload className="h-4 w-4" />Secure uploads</p>
            <div className="space-y-1.5"><Label>Resume / CV (required)</Label><Input data-testid="apply-resume-input" type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => setResume(e.target.files?.[0] || null)} className="bg-white" /></div>
            <div className="space-y-1.5"><Label>Licenses & certificates (optional, multiple)</Label><Input data-testid="apply-licenses-input" type="file" multiple onChange={(e) => setLicenses(Array.from(e.target.files || []))} className="bg-white" /></div>
            <p className="text-xs text-stone-500">Files are private. Only our team can see them until you choose to share them.</p>
            {error && <p data-testid="apply-error" className="text-sm text-red-600">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="apply-submit-button" className="h-11 w-full">{busy ? "Submitting…" : "Submit application"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
